import { AceCard } from '../AceCard';
import { DebtRegistryView } from '../DebtRegistryView';
import { useLoanApplication, usePerson } from '@axo/shared/data-access/hooks';
import {
  Icon,
  Spinner,
  Tab,
  TabId,
  TabPanel,
  Tabs,
  Tooltip,
} from '@blueprintjs/core';
import styles from './DebtRegistryCard.module.css';
import {
  SelectedApplicantTab,
  useApplicantsFormStore,
} from '../../forms/applicants/store';
import { useCallback } from 'react';

export function DebtRegistryCard({ applicationID }: { applicationID: string }) {
  const TABS_PARENT_ID = `application-data-tabs-${applicationID}`;
  const {
    selectedApplicant,
    actions: { setSelectedApplicant },
  } = useApplicantsFormStore(applicationID);
  const { data: applicationData } = useLoanApplication(applicationID);
  const { data: applicantData, isLoading: isLoadingApplicantData } = usePerson(
    applicationData?.PersonID,
  );
  const { data: coApplicantData, isLoading: isLoadingCoApplicantData } =
    usePerson(applicationData?.CoApplicantPersonID);

  const applicantName =
    (applicantData &&
      `${applicantData.FirstName ?? ''} ${applicantData.LastName ?? ''}`.trim()) ||
    undefined;
  const coApplicantName =
    (coApplicantData &&
      `${coApplicantData.FirstName ?? ''} ${coApplicantData.LastName ?? ''}`.trim()) ||
    undefined;

  const isLoading = isLoadingApplicantData || isLoadingCoApplicantData;

  const onTabChange = useCallback(
    (tabID: TabId) => {
      if (tabID === SelectedApplicantTab.CO_APPLICANT) {
        setSelectedApplicant(tabID);
      } else {
        setSelectedApplicant(SelectedApplicantTab.MAIN_APPLICANT);
      }
    },
    [setSelectedApplicant],
  );

  if (!applicationData) return null;

  return (
    <AceCard
      key={`DebtRegistry.${applicationID}`}
      id={`DebtRegistry.${applicationID}`}
      title="Debt registry"
      noBorder
      noContentPadding
      isExpandedByDefault={false}
      classes={{ content: styles.card }}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Tabs
            id={TABS_PARENT_ID}
            onChange={onTabChange}
            selectedTabId={selectedApplicant}
            className={styles.tabs}
          >
            <Tab
              id={SelectedApplicantTab.MAIN_APPLICANT}
              title={
                <div>
                  <Tooltip placement="top" content="Main applicant">
                    <span className={styles.tab}>
                      <Icon icon="person" />
                      <span>{applicantName ?? 'Main Applicant Data'}</span>
                    </span>
                  </Tooltip>
                </div>
              }
            />
            {coApplicantData && (
              <Tab
                id={SelectedApplicantTab.CO_APPLICANT}
                title={
                  <div>
                    <Tooltip placement="top" content="Co-applicant">
                      <span className={styles.tab}>
                        <Icon icon="people" />
                        <span>{coApplicantName ?? 'Co-applicant Data'}</span>
                      </span>
                    </Tooltip>
                  </div>
                }
              />
            )}
          </Tabs>
          {selectedApplicant === SelectedApplicantTab.MAIN_APPLICANT &&
            applicantData && (
              <TabPanel
                key={SelectedApplicantTab.MAIN_APPLICANT}
                id={SelectedApplicantTab.MAIN_APPLICANT}
                selectedTabId={selectedApplicant}
                parentId={TABS_PARENT_ID}
                className={styles.tabPanel}
                panel={
                  <DebtRegistryView
                    applicationID={applicationID}
                    customerID={applicationData.CustomerID}
                    personID={applicationData.PersonID ?? ''}
                  />
                }
              />
            )}
          {selectedApplicant === SelectedApplicantTab.CO_APPLICANT &&
            coApplicantData &&
            applicationData.CoApplicantCustomerID && (
              <TabPanel
                key={SelectedApplicantTab.CO_APPLICANT}
                id={SelectedApplicantTab.CO_APPLICANT}
                selectedTabId={selectedApplicant}
                parentId={TABS_PARENT_ID}
                className={styles.tabPanel}
                panel={
                  <DebtRegistryView
                    applicationID={applicationID}
                    customerID={applicationData.CoApplicantCustomerID}
                    personID={applicationData.CoApplicantPersonID ?? ''}
                  />
                }
              />
            )}
        </>
      )}
    </AceCard>
  );
}
